import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Input from '@zg-rentals/multibrand-ui/lib/Input';
import CurrencyInput from '@zg-rentals/multibrand-ui/lib/CurrencyInput';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Button from '@zg-rentals/multibrand-ui/lib/Button';

import OneTimePaymentTypes, { TypesWithMonthlyLimits } from '@common/constants/oneTimePaymentTypes';
import FaqComponent from '@common/components/FaqComponent';
import faqs from '@common/constants/faqs';
import { isOneTimePaymentNoteLengthValid, validateInputs } from './validation';
import { ONE_TIME_PAYMENT_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import errorMessages from '@common/constants/errorMessages';
import { dollarsToCents } from '@common/paymentsUtils/currencyUtils';
import type { OneTimePaymentRequestInterface } from '@rental-manager/pages/Dashboard/types';
import DebouncedButton from '@common/components/DebouncedButton';

const paymentTypeSelectOptions = {
  selector: 'Select One',
  [OneTimePaymentTypes.UTILITIES]: 'Utilities',
  [OneTimePaymentTypes.DEPOSIT]: 'Deposit',
  [OneTimePaymentTypes.PRORATED_RENT]: 'Prorated rent',
  [OneTimePaymentTypes.LATE_FEE]: 'Late fee',
  [OneTimePaymentTypes.PET_FEE]: 'Pet fee',
  [OneTimePaymentTypes.OTHER]: 'Other',
};

type Props = {
  selectedTenantIndex: number;
  rentTermsKeyArray: Array<string>;
  tenantNameArray: Array<string>;
  oneTimePaymentLimitsArray: Array<Record<string, unknown>>;
  onSubmit: (args: OneTimePaymentRequestInterface) => void;
  onCancel: () => void;
  handleTenantChange: (index: number) => void;
};

type State = {
  oneTimePaymentType: string;
  oneTimePaymentNotes: string;
  oneTimePaymentAmount: string;
  errors: {
    oneTimePaymentType: string;
    oneTimePaymentNotes: string;
    oneTimePaymentAmount: string;
    tenantName: string;
  };
};

class RequestPage extends React.Component<Props, State> {
  state = {
    oneTimePaymentType: '',
    oneTimePaymentNotes: '',
    oneTimePaymentAmount: '',
    errors: {
      oneTimePaymentType: '',
      oneTimePaymentNotes: '',
      oneTimePaymentAmount: '',
      tenantName: '',
    },
  };

  getOneTimePaymentOptions = (): React.ReactNode => {
    const { oneTimePaymentLimitsArray, selectedTenantIndex } = this.props;
    const oneTimePaymentLimits = oneTimePaymentLimitsArray[selectedTenantIndex];
    return Object.entries(paymentTypeSelectOptions)
      .filter(([key]) => key === 'selector' || oneTimePaymentLimits[key])
      .map(([key, value]) => (
        <option key={key} value={key}>
          {value}
        </option>
      ));
  };

  getTenantNameOptions = (): React.ReactNode => {
    const { tenantNameArray, oneTimePaymentLimitsArray } = this.props;
    return tenantNameArray.map((tenantName, index) => {
      const isEmpty = Object.keys(oneTimePaymentLimitsArray[index]).length === 0;
      return (
        <option key={index} value={index} disabled={isEmpty}>
          {tenantName}
        </option>
      );
    });
  };

  getPaymentTypeHelperText = (): React.ReactNode => {
    const showMonthlyLimitText = TypesWithMonthlyLimits.includes(this.state.oneTimePaymentType);
    return (
      showMonthlyLimitText && (
        <Text type="body-tiny" lowlight className="ptm">
          1 request allowed per month
        </Text>
      )
    );
  };

  getNotesHelperText = (): string => {
    const notesHelp = `${this.state.oneTimePaymentNotes.length}/${ONE_TIME_PAYMENT_NOTE_MAX_LENGTH}`;
    return this.state.errors.oneTimePaymentNotes ? '' : notesHelp;
  };

  handlePaymentTypeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      oneTimePaymentType: e.target.value,
      errors: { ...this.state.errors, oneTimePaymentType: '' },
    });
  };

  handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let value = e.target.value;
    let error = '';

    if (!isOneTimePaymentNoteLengthValid(value)) {
      value = value.substring(0, ONE_TIME_PAYMENT_NOTE_MAX_LENGTH);
      error = errorMessages.ONE_TIME_PAYMENT_NOTE_MAX_LENGTH;
    }

    this.setState({
      oneTimePaymentNotes: value,
      errors: { ...this.state.errors, oneTimePaymentNotes: error },
    });
  };

  handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      oneTimePaymentAmount: e.target.value,
      errors: { ...this.state.errors, oneTimePaymentAmount: '' },
    });
  };

  handleTenantChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.props.handleTenantChange(Number(e.target.value));
  };

  onSubmit = async (): Promise<void> => {
    if (!validateInputs(this)) {
      return;
    }

    const request: OneTimePaymentRequestInterface = {
      rentTermsKey: this.props.rentTermsKeyArray[this.props.selectedTenantIndex],
      amount: dollarsToCents(this.state.oneTimePaymentAmount),
      type: this.state.oneTimePaymentType,
      notes: this.state.oneTimePaymentNotes,
    };
    await this.props.onSubmit(request);
  };

  render(): React.ReactNode {
    const mustSelectTenant = this.props.rentTermsKeyArray.length > 1;

    return (
      <>
        <Row>
          <Col>
            <Text type="h3">Payment request</Text>
          </Col>
        </Row>
        <Row>
          <Col>
            <Select
              label="What's it for?"
              error={this.state.errors.oneTimePaymentType}
              onChange={this.handlePaymentTypeChange}
            >
              {this.getOneTimePaymentOptions()}
            </Select>
            {this.getPaymentTypeHelperText()}
          </Col>
        </Row>
        <Row>
          <Col>
            <Input
              placeholder="Enter notes (example: June Electricity)"
              value={this.state.oneTimePaymentNotes}
              error={this.state.errors.oneTimePaymentNotes}
              help={this.getNotesHelperText()}
              onChange={this.handleNotesChange}
              onBlur={this.handleNotesChange}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <CurrencyInput
              label="Amount"
              value={this.state.oneTimePaymentAmount}
              error={this.state.errors.oneTimePaymentAmount}
              onChange={this.handleAmountChange}
              onBlur={this.handleAmountChange}
              onFocus={this.handleAmountChange}
            />
          </Col>
        </Row>
        {mustSelectTenant && (
          <Row>
            <Col>
              <Select label="Who's it for?" error={this.state.errors.tenantName} onChange={this.handleTenantChange}>
                {this.getTenantNameOptions()}
              </Select>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <FaqComponent.Landlord
              faqs={faqs.ONE_TIME_PAYMENT_IRENE.body}
              title={faqs.ONE_TIME_PAYMENT_IRENE.title}
              showContactSection
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <DebouncedButton type="primary" full onClick={this.onSubmit}>
              Request a one-time payment
            </DebouncedButton>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button type="tertiary" full onClick={this.props.onCancel}>
              Cancel
            </Button>
          </Col>
        </Row>
      </>
    );
  }
}

export default RequestPage;
